import {
  FaCalendarCheck,
  FaCameraRetro,
  FaWandMagicSparkles,
  FaCalendarDays,
  FaClipboardCheck,
  FaFileLines,
  FaListCheck,
  FaUserCheck,
} from "react-icons/fa6";

import type { FeatureModule } from "./types";

export const akademikModules: FeatureModule[] = [
  {
    slug: "presensi-harian",
    label: "Presensi Harian",
    navDescription: "Tap kartu, keterlambatan, rekap",
    icon: FaUserCheck,
    group: "akademik",
    summary:
      "Presensi masuk dan pulang lewat tap kartu, dengan aturan jam yang bisa berbeda tiap hari dan tiap peran.",
    detail: {
      title: "Presensi yang selesai *saat anak menempelkan kartu*",
      lead: "Tap kartu di gerbang, rekap tersusun sendiri, dan orang tua tahu tanpa perlu bertanya",
      intro:
        "Tidak ada lagi absensi manual yang direkap ulang tiap akhir bulan. Siswa dan guru menempelkan kartu saat masuk dan pulang, sistem mencocokkannya dengan jadwal presensi sekolah, lalu menandai siapa yang tepat waktu dan siapa yang terlambat.",
      highlights: [
        "Jam masuk dan pulang bisa diatur berbeda tiap hari",
        "Aturan terpisah untuk siswa dan untuk guru & staf",
        "Batas maksimal keterlambatan menentukan status otomatis",
      ],
      benefits: [
        {
          title: "Aturan mengikuti kebiasaan sekolah",
          body: "Atur jam masuk, jam pulang, dan batas keterlambatan per hari. Jadwal Senin bisa berbeda dari Jumat, dan aturan guru bisa berbeda dari siswa.",
        },
        {
          title: "Keterlambatan terdeteksi sendiri",
          body: "Sistem membandingkan waktu tap dengan batas maksimal keterlambatan, lalu memisahkan daftar siswa terlambat tanpa perlu ada yang menghitung manual.",
        },
        {
          title: "Rekap siswa dan guru terpisah",
          body: "Rekap presensi siswa dan rekap guru & staf berdiri sendiri, jadi wali kelas dan bagian kepegawaian melihat data yang relevan bagi masing-masing.",
        },
        {
          title: "Ada jalan keluar untuk kasus khusus",
          body: "Kartu tertinggal atau alat bermasalah tetap bisa ditangani lewat presensi manual, yang tercatat sebagai penambahan data dengan jejak perubahannya.",
        },
      ],
      screensTitle: "Dari pengaturan sampai rekap",
      screensLead:
        "Atur sekali di awal tahun ajaran, lalu yang tersisa hanya membaca rekapnya.",
      screens: [
        {
          src: "/image/fitur/presensi-harian/desktop-pengaturan.webp",
          alt: "Pengaturan waktu presensi harian per hari untuk siswa dan guru",
          caption: "Pengaturan Waktu Presensi",
          note: "Jam masuk, pulang, dan batas keterlambatan per hari",
          kind: "desktop",
        },
        {
          src: "/image/fitur/presensi-harian/desktop-rekap-siswa.webp",
          alt: "Rekap presensi harian siswa di dashboard Cards School",
          caption: "Rekap Presensi Siswa",
          note: "Rekap per siswa dengan pencarian dan filter",
          kind: "desktop",
        },
        {
          src: "/image/fitur/presensi-harian/mobile-presensi.webp",
          alt: "Tampilan presensi harian anak di aplikasi orang tua",
          caption: "Presensi di aplikasi orang tua",
          note: "Orang tua melihat kehadiran anak per hari",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Bagaimana kalau jam masuk tiap hari berbeda?",
          a: "Jadwal presensi diatur per hari, dari Senin sampai Minggu. Anda bisa menetapkan jam mulai, jam selesai, dan batas maksimal keterlambatan yang berbeda untuk tiap harinya.",
        },
        {
          q: "Apakah presensi guru dan siswa memakai aturan yang sama?",
          a: "Tidak harus. Ada opsi untuk memisahkan waktu presensi siswa dengan waktu presensi guru dan staf, masing-masing dengan jadwalnya sendiri.",
        },
        {
          q: "Kalau siswa lupa membawa kartu?",
          a: "Petugas dapat menambahkan presensi manual dari dashboard. Data tersebut tercatat sebagai entri manual sehingga tetap terlihat pada rekap dan dapat diaudit.",
        },
      ],
      metaTitle: "Aplikasi Presensi Harian Sekolah dengan Kartu RFID",
      metaDescription:
        "Presensi masuk dan pulang lewat tap kartu RFID. Atur jam dan batas keterlambatan per hari, pisahkan aturan siswa dan guru, rekap tersusun otomatis.",
      keywords: [
        "aplikasi presensi sekolah",
        "absensi siswa rfid",
        "sistem absensi sekolah online",
        "presensi harian siswa",
        "aplikasi absensi guru",
      ],
    },
  },

  {
    slug: "presensi-mapel",
    label: "Presensi Mapel",
    navDescription: "Input per jam pelajaran & rekap",
    icon: FaClipboardCheck,
    group: "akademik",
    summary:
      "Guru menginput kehadiran per mata pelajaran, dan rekapnya tersusun per siswa.",
    detail: {
      title: "Kehadiran per jam pelajaran, *bukan hanya per hari*",
      lead: "Guru menginput presensi sesuai jadwal mengajarnya, lengkap dengan catatan pembelajaran",
      intro:
        "Hadir di gerbang belum tentu hadir di kelas. Presensi mapel menutup celah itu: guru menginput kehadiran pada jam pelajarannya sendiri, bisa memilih beberapa siswa sekaligus, dan sekalian mencatat materi yang diajarkan hari itu.",
      highlights: [
        "Input presensi mengikuti jadwal mengajar guru",
        "Pilih beberapa siswa sekaligus, tidak satu per satu",
        "Catatan pembelajaran dan QR presensi dalam alur yang sama",
      ],
      benefits: [
        {
          title: "Terikat pada jadwal mengajar",
          body: "Guru hanya bisa menginput presensi pada jadwal mengajarnya. Kalau jadwalnya tidak ditemukan, sistem memberi tahu alih-alih menerima data yang keliru.",
        },
        {
          title: "Input yang tidak melelahkan",
          body: "Beberapa siswa dapat dipilih sekaligus lalu ditandai bersamaan, jadi mengisi presensi satu kelas tidak menghabiskan jam pelajaran.",
        },
        {
          title: "Materi ikut terekam",
          body: "Catatan pembelajaran diisi berdampingan dengan presensi. Guru bisa memilih materi yang sudah ada atau menambahkan materi baru.",
        },
        {
          title: "Riwayat yang bisa ditelusuri",
          body: "Riwayat presensi mapel dapat dicari dan difilter, memudahkan wali kelas menelusuri pola ketidakhadiran seorang siswa di mata pelajaran tertentu.",
        },
      ],
      screensTitle: "Alur guru di satu layar",
      screensLead:
        "Dari memilih jadwal, menandai kehadiran, sampai menuliskan materi — semua dalam satu rangkaian.",
      screens: [
        {
          src: "/image/fitur/presensi-mapel/desktop-input.webp",
          alt: "Halaman input presensi mata pelajaran di dashboard Cards School",
          caption: "Input Presensi",
          note: "Mengikuti jadwal mengajar guru",
          kind: "desktop",
        },
        {
          src: "/image/fitur/presensi-mapel/desktop-kehadiran.webp",
          alt: "Menandai kehadiran beberapa siswa sekaligus",
          caption: "Presensi Kehadiran",
          note: "Pilih beberapa siswa dan tandai bersamaan",
          kind: "desktop",
        },
        {
          src: "/image/fitur/presensi-mapel/desktop-catatan.webp",
          alt: "Catatan pembelajaran dengan pilihan materi",
          caption: "Catatan Pembelajaran",
          note: "Pilih materi lama atau tambahkan materi baru",
          kind: "desktop",
        },
      ],
      faq: [
        {
          q: "Apakah guru bisa menginput presensi di luar jam mengajarnya?",
          a: "Tidak. Input presensi terikat pada jadwal mengajar, dan sistem menolak bila jadwal untuk waktu tersebut tidak ditemukan.",
        },
        {
          q: "Bagaimana hubungan presensi mapel dengan presensi harian?",
          a: "Keduanya berdiri sendiri. Presensi harian mencatat kehadiran di sekolah lewat tap kartu, sedangkan presensi mapel mencatat kehadiran pada tiap jam pelajaran.",
        },
        {
          q: "Apakah tersedia presensi lewat QR?",
          a: "Ya, QR presensi tersedia sebagai salah satu cara pengambilan kehadiran pada mata pelajaran, berdampingan dengan input manual oleh guru.",
        },
      ],
      metaTitle: "Presensi Mata Pelajaran & Catatan Pembelajaran",
      metaDescription:
        "Guru menginput kehadiran per jam pelajaran sesuai jadwal mengajar, memilih beberapa siswa sekaligus, dan mencatat materi pembelajaran di alur yang sama.",
      keywords: [
        "presensi mata pelajaran",
        "absensi per jam pelajaran",
        "jurnal mengajar guru",
        "catatan pembelajaran digital",
        "aplikasi presensi guru",
      ],
    },
  },

  {
    slug: "presensi-kegiatan",
    label: "Presensi Kegiatan",
    navDescription: "Sholat jamaah, ekskul, kegiatan",
    icon: FaCalendarCheck,
    group: "akademik",
    summary:
      "Presensi untuk kegiatan di luar jam pelajaran, dengan rekap per kegiatan maupun per siswa.",
    detail: {
      title: "Kehadiran di kegiatan pun *punya catatannya sendiri*",
      lead: "Sholat berjamaah, ekstrakurikuler, dan kegiatan sekolah lain dengan rekap dua arah",
      intro:
        "Yang terjadi di luar jam pelajaran sering kali paling sulit didata, padahal justru di situ pembinaan berlangsung. Presensi kegiatan memberi setiap agenda daftar hadirnya sendiri, dengan rekap yang bisa dibaca per kegiatan maupun per siswa.",
      highlights: [
        "Satu kegiatan, satu daftar hadir dan pengaturannya sendiri",
        "Rekap per kegiatan dan rekap per siswa",
        "Berlaku untuk siswa maupun guru & staf",
      ],
      benefits: [
        {
          title: "Kegiatan diatur sendiri-sendiri",
          body: "Sholat berjamaah, ekstrakurikuler, atau agenda tahunan punya pengaturan presensi masing-masing, bukan dipaksa mengikuti satu jadwal yang sama.",
        },
        {
          title: "Dua cara membaca data",
          body: "Rekap per kegiatan menjawab siapa yang hadir di suatu agenda; rekap per siswa menjawab seberapa aktif seorang anak mengikuti kegiatan.",
        },
        {
          title: "Pembina ikut terdata",
          body: "Presensi kegiatan juga mencakup guru dan staf, sehingga kehadiran pembina dapat ditelusuri sama seperti kehadiran peserta.",
        },
        {
          title: "Bahan pembinaan yang nyata",
          body: "Data kehadiran kegiatan menjadi dasar objektif untuk penilaian sikap dan pembinaan, bukan sekadar kesan pembina.",
        },
      ],
      screensTitle: "Kegiatan anak di aplikasi orang tua",
      screensLead:
        "Orang tua bisa melihat sendiri kegiatan apa yang diikuti anaknya dan seberapa rutin kehadirannya.",
      screens: [
        {
          src: "/image/fitur/presensi-kegiatan/mobile-kegiatan.webp",
          alt: "Presensi kegiatan siswa di aplikasi CARDS untuk orang tua",
          caption: "Presensi Kegiatan",
          note: "Riwayat kehadiran anak pada tiap kegiatan",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Kegiatan apa saja yang bisa didata?",
          a: "Kegiatan ditentukan sekolah — sholat berjamaah, mengaji, ekstrakurikuler, upacara, atau agenda lain. Masing-masing punya pengaturan presensinya sendiri.",
        },
        {
          q: "Apakah presensi kegiatan memakai kartu yang sama?",
          a: "Ya. Kartu siswa yang dipakai untuk presensi harian juga dipakai untuk presensi kegiatan, sehingga tidak perlu perangkat identitas tambahan.",
        },
        {
          q: "Bisakah rekap kegiatan diambil per siswa?",
          a: "Bisa. Tersedia rekap per kegiatan dan rekap per siswa, sehingga data yang sama dapat dibaca dari dua sudut sesuai kebutuhan.",
        },
      ],
      metaTitle: "Presensi Kegiatan Sekolah & Ekstrakurikuler",
      metaDescription:
        "Data kehadiran sholat berjamaah, ekstrakurikuler, dan kegiatan sekolah lain dengan kartu siswa. Rekap tersedia per kegiatan maupun per siswa.",
      keywords: [
        "presensi kegiatan sekolah",
        "absensi ekstrakurikuler",
        "presensi sholat berjamaah",
        "kehadiran kegiatan siswa",
        "rekap kegiatan siswa",
      ],
    },
  },

  {
    slug: "rapor",
    label: "Rapor Digital",
    navDescription: "Nilai akademik & sikap",
    icon: FaFileLines,
    group: "akademik",
    summary:
      "Rapor yang disusun digital dan bisa dibuka orang tua langsung dari aplikasi.",
    detail: {
      title: "Rapor yang bisa dibuka *kapan pun, bukan sekali setahun*",
      lead: "Nilai akademik dan penilaian sikap tersusun digital dan langsung sampai ke orang tua",
      intro:
        "Rapor kertas hanya berpindah tangan dua kali setahun, lalu tersimpan di lemari. Rapor digital membuat perkembangan anak bisa dibuka kapan saja dari aplikasi, tanpa perlu menunggu hari pembagian atau khawatir berkasnya hilang.",
      highlights: [
        "Nilai akademik dan penilaian sikap dalam satu rapor",
        "Diakses orang tua langsung dari aplikasi CARDS",
        "Terhubung dengan data presensi yang sudah terekam",
      ],
      benefits: [
        {
          title: "Tidak menunggu hari pembagian",
          body: "Orang tua membuka rapor dari aplikasi kapan pun. Perkembangan anak jadi bahan percakapan sepanjang semester, bukan kejutan di akhir.",
        },
        {
          title: "Akademik dan sikap berdampingan",
          body: "Rapor memuat nilai akademik sekaligus penilaian sikap, jadi gambaran tentang anak tidak berhenti pada angka mata pelajaran.",
        },
        {
          title: "Berdiri di atas data yang sudah ada",
          body: "Presensi dan catatan kesiswaan sudah terekam di sistem yang sama, sehingga penyusunan rapor tidak dimulai dari mengumpulkan data yang tercecer.",
        },
        {
          title: "Berkas yang tidak hilang",
          body: "Rapor tersimpan di sistem, jadi tidak ada lagi permintaan cetak ulang karena lembarnya terselip atau rusak.",
        },
      ],
      screensTitle: "Rapor di genggaman orang tua",
      screensLead:
        "Dibuka dari menu Akademik, sejajar dengan presensi dan jadwal pelajaran.",
      screens: [
        {
          src: "/image/fitur/rapor/mobile-rapor.webp",
          alt: "Rapor siswa di aplikasi CARDS untuk orang tua",
          caption: "Rapor Siswa",
          note: "Nilai akademik dan penilaian sikap",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah format rapor bisa mengikuti kurikulum kami?",
          a: "Penyusunan rapor disesuaikan pada tahap implementasi, mengikuti komponen penilaian yang berlaku di sekolah Anda. Tim kami akan membahasnya bersama bagian kurikulum.",
        },
        {
          q: "Siapa yang bisa melihat rapor seorang siswa?",
          a: "Rapor terbuka bagi orang tua siswa bersangkutan lewat akun aplikasinya, serta bagi peran di sekolah yang Anda beri izin lewat pengaturan pengguna.",
        },
        {
          q: "Apakah rapor digital menggantikan rapor cetak?",
          a: "Rapor digital membuat nilai dapat diakses kapan saja. Kebutuhan cetak untuk keperluan administratif tetap dapat dipenuhi sesuai kebijakan sekolah.",
        },
      ],
      metaTitle: "Rapor Digital Siswa yang Diakses Orang Tua",
      metaDescription:
        "Nilai akademik dan penilaian sikap tersusun digital dan dapat dibuka orang tua kapan saja dari aplikasi CARDS, terhubung dengan data presensi sekolah.",
      keywords: [
        "rapor digital siswa",
        "e-rapor sekolah",
        "aplikasi rapor online",
        "nilai siswa online",
        "rapor sikap siswa",
      ],
    },
  },

  {
    slug: "tugas-jadwal",
    label: "Tugas & Jadwal Pelajaran",
    navDescription: "Tugas guru & jadwal digital",
    icon: FaListCheck,
    group: "akademik",
    summary:
      "Guru membagikan tugas, siswa dan orang tua melihat jadwal pelajaran yang selalu terbarui.",
    detail: {
      title: "Tugas yang *tidak berhenti di papan tulis*",
      lead: "Guru memberi tugas dengan tenggat dan lampiran, lalu memantau pengumpulan sampai penilaiannya",
      intro:
        "Tugas yang hanya disampaikan lisan mudah terlupakan, dan orang tua sering tidak tahu ada tenggat yang mendekat. Lewat aplikasi CARDS Edu, guru membuat tugas beserta tanggal dan jamnya, melampirkan materi, lalu memantau siapa yang sudah mengumpulkan dan menilai hasilnya.",
      highlights: [
        "Tugas dengan tenggat tanggal dan jam, plus lampiran materi",
        "Status tiap siswa: ditugaskan, terkumpul, dinilai, berakhir",
        "Input nilai dan filter hasil langsung dari aplikasi guru",
      ],
      benefits: [
        {
          title: "Tenggat yang jelas sejak awal",
          body: "Guru menetapkan tanggal dan jam berakhirnya tugas saat membuatnya, sehingga tidak ada perdebatan soal kapan batas pengumpulan.",
        },
        {
          title: "Materi ikut terlampir",
          body: "Berkas pendukung dilampirkan pada tugas dan bisa dihapus atau diganti, jadi siswa tidak perlu mencari sumbernya sendiri.",
        },
        {
          title: "Terlihat siapa yang tertinggal",
          body: "Status per siswa memisahkan yang baru ditugaskan, sudah terkumpul, sudah dinilai, dan yang tenggatnya sudah berakhir.",
        },
        {
          title: "Penilaian di aplikasi yang sama",
          body: "Nilai diinput langsung dari aplikasi guru, dan hasilnya bisa difilter — termasuk melihat nilai tertinggi di satu kelas.",
        },
      ],
      screensTitle: "Dari membuat tugas sampai menilai",
      screensLead:
        "Tiga layar yang dipakai guru berurutan, semuanya dari aplikasi CARDS Edu.",
      screens: [
        {
          src: "/image/fitur/tugas-jadwal/mobile-tambah.webp",
          alt: "Membuat tugas baru dengan pemilihan tanggal di aplikasi guru",
          caption: "Tambah Tugas",
          note: "Tetapkan kelas, tenggat, dan lampiran",
          kind: "mobile",
        },
        {
          src: "/image/fitur/tugas-jadwal/mobile-detail.webp",
          alt: "Detail tugas dengan daftar siswa yang ditugaskan",
          caption: "Detail Tugas",
          note: "Status tiap siswa atas tugas tersebut",
          kind: "mobile",
        },
        {
          src: "/image/fitur/tugas-jadwal/mobile-nilai.webp",
          alt: "Input nilai tugas siswa di aplikasi guru",
          caption: "Input Nilai",
          note: "Menilai hasil dan menyaring capaian kelas",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Apakah orang tua bisa melihat tugas anaknya?",
          a: "Jadwal pelajaran dan informasi akademik anak tersedia di aplikasi orang tua, sehingga orang tua dapat mengikuti kegiatan belajar tanpa harus bertanya ke guru.",
        },
        {
          q: "Bisakah tugas diberikan ke lebih dari satu kelas?",
          a: "Saat membuat tugas, guru memilih kelas tujuan lewat pencarian kelas, sehingga tugas dapat diarahkan sesuai rombel yang diampu.",
        },
        {
          q: "Apa yang terjadi setelah tenggat berakhir?",
          a: "Tugas berpindah ke status berakhir, dan daftar siswa memisahkan yang sudah mengumpulkan dari yang belum, sehingga guru tahu siapa yang perlu ditindaklanjuti.",
        },
      ],
      metaTitle: "Tugas Siswa & Jadwal Pelajaran Digital",
      metaDescription:
        "Guru membuat tugas dengan tenggat dan lampiran, memantau pengumpulan per siswa, lalu menilai dari aplikasi. Jadwal pelajaran terbuka untuk orang tua.",
      keywords: [
        "aplikasi tugas siswa",
        "jadwal pelajaran digital",
        "pemberian tugas online",
        "penilaian tugas guru",
        "cards edu guru",
      ],
    },
  },

  {
    slug: "kalender-akademik",
    label: "Kalender Akademik",
    navDescription: "Agenda sekolah sepanjang tahun",
    icon: FaCalendarDays,
    group: "akademik",
    summary:
      "Kalender pendidikan yang bisa diakses guru, siswa, dan orang tua dari satu sumber.",
    detail: {
      title: "Satu kalender yang *dibaca semua orang*",
      lead: "Agenda sekolah, libur, dan kegiatan penting dalam kalender pendidikan yang terbuka",
      intro:
        "Kalender pendidikan yang hanya tertempel di ruang guru tidak sampai ke orang tua. Ketika dibawa ke aplikasi, semua pihak membaca sumber yang sama — jadwal ujian, hari libur, dan agenda sekolah tidak lagi tersebar lewat pesan berantai.",
      highlights: [
        "Agenda sekolah tersusun per bulan sepanjang tahun ajaran",
        "Dibuka guru, siswa, dan orang tua dari aplikasi yang sama",
        "Menggantikan pengumuman berantai yang mudah tercecer",
      ],
      benefits: [
        {
          title: "Tidak ada dua versi jadwal",
          body: "Karena semua membaca kalender yang sama, tidak ada lagi perbedaan informasi antara yang diterima orang tua dan yang berlaku di sekolah.",
        },
        {
          title: "Orang tua bisa merencanakan",
          body: "Hari libur dan agenda penting terlihat jauh hari, sehingga keluarga dapat mengatur jadwalnya tanpa mendadak.",
        },
        {
          title: "Selalu tersedia di aplikasi",
          body: "Kalender ada di menu Akademik bersama presensi dan rapor, jadi tidak perlu mencari selebaran atau menggulir grup pesan.",
        },
        {
          title: "Perubahan sampai ke semua",
          body: "Ketika agenda bergeser, perubahannya berlaku di satu tempat dan langsung terbaca oleh semua yang membukanya.",
        },
      ],
      screensTitle: "Kalender pendidikan per bulan",
      screensLead:
        "Ditampilkan bulanan supaya agenda terdekat langsung terbaca saat dibuka.",
      screens: [
        {
          src: "/image/fitur/kalender-akademik/mobile-kalender.webp",
          alt: "Kalender pendidikan bulanan di aplikasi CARDS",
          caption: "Kalender Pendidikan",
          note: "Agenda dan hari penting per bulan",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Siapa yang mengisi kalender akademik?",
          a: "Kalender dikelola dari dashboard sekolah oleh peran yang Anda beri izin, lalu tampil di aplikasi guru, siswa, dan orang tua.",
        },
        {
          q: "Apakah kalender bisa memuat agenda per jenjang?",
          a: "Agenda disusun mengikuti kebutuhan sekolah. Rinciannya dibahas pada tahap implementasi bersama bagian kurikulum Anda.",
        },
        {
          q: "Apakah orang tua mendapat pemberitahuan agenda?",
          a: "Untuk hal yang perlu disampaikan aktif, sekolah dapat memakai modul Pengumuman yang tersiar ke aplikasi dan WhatsApp orang tua.",
        },
      ],
      metaTitle: "Kalender Akademik Sekolah Online",
      metaDescription:
        "Kalender pendidikan yang dibaca guru, siswa, dan orang tua dari satu sumber. Agenda, hari libur, dan kegiatan sekolah tersusun per bulan.",
      keywords: [
        "kalender akademik sekolah",
        "kalender pendidikan digital",
        "agenda sekolah online",
        "jadwal kegiatan sekolah",
        "kalender sekolah aplikasi",
      ],
    },
  },
  {
    slug: "presensi-wajah",
    label: "Presensi Wajah",
    navDescription: "Face recognition & perangkat presensi",
    icon: FaCameraRetro,
    group: "akademik",
    summary:
      "Presensi lewat pengenalan wajah pada perangkat di sekolah — pelengkap tap kartu, dengan log per perangkat.",
    detail: {
      title: "Hadir cukup dengan *menghadap perangkat*",
      lead: "Pengenalan wajah melengkapi tap kartu: data wajah tersinkron ke perangkat, dan setiap presensi meninggalkan log",
      intro:
        "Kartu bisa tertinggal; wajah tidak. Perangkat presensi wajah di gerbang atau anjungan mengenali siswa dan staf yang datang, mencatat kehadirannya, dan menyimpan lognya per perangkat — berjalan berdampingan dengan presensi tap kartu yang sudah ada.",
      highlights: [
        "Pengenalan wajah pada perangkat presensi di sekolah",
        "Data wajah tersinkron dari sistem ke tiap perangkat",
        "Log presensi per perangkat yang bisa ditelusuri",
      ],
      benefits: [
        {
          title: "Tidak tergantung kartu di saku",
          body: "Siswa yang kartunya tertinggal tetap tercatat hadir — wajah menjadi identitas yang selalu terbawa.",
        },
        {
          title: "Perangkat dikelola dari dashboard",
          body: "Data device presensi terdaftar di sistem, dan sinkronisasi data wajah ke perangkat dilakukan dari sana — bukan diatur satu-satu di alatnya.",
        },
        {
          title: "Setiap tap wajah ada lognya",
          body: "Log presensi perangkat wajah menyimpan siapa dikenali di perangkat mana dan kapan, sehingga anomali mudah ditelusuri.",
        },
        {
          title: "Satu rekap dengan presensi kartu",
          body: "Hasil presensi wajah masuk ke rekap presensi harian yang sama, jadi wali kelas tidak membaca dua laporan terpisah.",
        },
      ],
      screensTitle: "Dari pendaftaran wajah sampai rekap",
      screensLead:
        "Alur yang berjalan sekali di awal, lalu bekerja sendiri setiap pagi.",
      steps: [
        {
          title: "Daftarkan perangkat",
          body: "Perangkat presensi wajah didaftarkan pada data device presensi di dashboard sekolah.",
        },
        {
          title: "Sinkronkan data wajah",
          body: "Data wajah anggota disinkronkan dari sistem ke perangkat melalui menu sync wajah.",
        },
        {
          title: "Siswa hadap perangkat",
          body: "Saat datang, siswa atau staf cukup menghadap perangkat; kehadiran tercatat seketika.",
        },
        {
          title: "Baca log dan rekapnya",
          body: "Log per perangkat dapat ditelusuri, dan hasilnya menyatu ke rekap presensi harian sekolah.",
        },
      ],
      faq: [
        {
          q: "Apakah presensi wajah menggantikan tap kartu?",
          a: "Tidak — keduanya berjalan berdampingan. Sekolah bisa memakai kartu, wajah, atau keduanya sesuai perangkat yang tersedia di tiap titik.",
        },
        {
          q: "Bagaimana data wajah sampai ke perangkat?",
          a: "Melalui sinkronisasi dari dashboard: data wajah anggota dikirim ke perangkat yang terdaftar, sehingga penambahan siswa baru tidak perlu didaftarkan manual di alat.",
        },
        {
          q: "Apakah lognya bisa diaudit?",
          a: "Ya. Log presensi perangkat wajah mencatat setiap pengenalan per perangkat dan dapat ditelusuri dari dashboard.",
        },
      ],
      metaTitle: "Presensi Face Recognition untuk Sekolah",
      metaDescription:
        "Presensi wajah untuk siswa, guru, dan staf: data wajah tersinkron ke perangkat, log per perangkat tercatat, dan hasilnya menyatu dengan rekap presensi harian.",
      keywords: [
        "absensi face recognition sekolah",
        "presensi wajah siswa",
        "mesin absensi wajah sekolah",
        "face recognition pesantren",
        "perangkat presensi sekolah",
      ],
    },
  },

  {
    slug: "kuis-ai",
    label: "Kuis AI",
    navDescription: "Generate soal otomatis dengan AI",
    icon: FaWandMagicSparkles,
    group: "akademik",
    summary:
      "Buat soal kuis dan flashcard secara otomatis dengan AI dari materi yang guru unggah, lengkap dengan jadwal dan rekapnya.",
    detail: {
      title: "Soal kuis yang *dibuatkan AI* dari materi guru sendiri",
      lead: "Unggah materi atau ambil dari jurnal guru, tentukan jumlah dan bobotnya, dan AI menyusun soalnya",
      intro:
        "Menyusun soal adalah pekerjaan berjam-jam yang berulang setiap pekan. Kuis AI memangkasnya: guru memasukkan materi — lewat unggahan berkas, jurnal guru, atau tulisan langsung — lalu AI membangkitkan soal kuis atau flashcard yang masih bisa disunting sebelum disimpan ke bank soal, dijadwalkan, dan direkap hasilnya.",
      highlights: [
        "Generate soal otomatis: pilih tipe kuis atau flashcard",
        "Sumber materi dari unggahan berkas, jurnal guru, atau tulisan",
        "Bank soal, jadwal kuis, halaman peserta, dan rekap hasil",
      ],
      benefits: [
        {
          title: "Jam menyusun soal jadi menit",
          body: "Tentukan konfigurasi — tipe soal, jumlah, dan bobot — lalu AI menyusunnya dari materi yang Anda berikan. Sisa waktunya kembali untuk mengajar.",
        },
        {
          title: "Soal tetap milik guru",
          body: "Hasil generate bisa disunting per soal atau dibangkitkan ulang sebelum disimpan. AI menyiapkan draf; keputusan tetap di tangan guru.",
        },
        {
          title: "Sekali dibuat, tersimpan di bank soal",
          body: "Soal yang sudah jadi masuk ke koleksi kuis dan dapat dipakai ulang lintas kelas maupun tahun ajaran.",
        },
        {
          title: "Dari jadwal sampai rekap nilai",
          body: "Kuis dijadwalkan, siswa mengerjakannya lewat halaman peserta, dan hasilnya terekap otomatis — satu alur penuh tanpa lembar jawaban kertas.",
        },
      ],
      screensTitle: "Dari konfigurasi sampai koleksi soal",
      screensLead:
        "Guru mengonfigurasi kuisnya, AI menyusun soal, siswa mengerjakan dari perangkat mana pun, dan semua grup soal terkumpul di koleksi kuis.",
      screens: [
        {
          src: "/image/fitur/kuis-ai/desktop-buat-kuis.webp",
          alt: "Form Buat Kuis dengan pilihan tipe soal kuis atau flashcard di dashboard Cards School",
          caption: "Konfigurasi Kuis",
          note: "Pilih kuis atau flashcard, lengkap dengan jenis, jumlah, dan bobot soalnya",
          kind: "desktop",
        },
        {
          src: "/image/fitur/kuis-ai/desktop-pengerjaan.webp",
          alt: "Halaman pengerjaan kuis dengan navigasi soal, sisa waktu, dan tombol kumpulkan jawaban",
          caption: "Halaman Pengerjaan",
          note: "Siswa mengerjakan soal dengan navigasi per nomor dan batas waktu",
          kind: "desktop",
        },
        {
          src: "/image/fitur/kuis-ai/desktop-koleksi.webp",
          alt: "Tabel koleksi kuis hasil generate AI dengan filter, pencarian, dan ekspor Excel",
          caption: "Koleksi Kuis",
          note: "Grup soal hasil generate terkumpul rapi, siap dipakai ulang atau diekspor",
          kind: "desktop",
        },
        {
          src: "/image/fitur/kuis-ai/mobile-pengerjaan.webp",
          alt: "Tampilan pengerjaan kuis di layar ponsel",
          caption: "Pengerjaan dari ponsel",
          note: "Kuis yang sama nyaman dikerjakan siswa langsung dari ponselnya",
          kind: "mobile",
        },
        {
          src: "/image/fitur/kuis-ai/mobile-hasil.webp",
          alt: "Tampilan nilai dan rekap jawaban benar setelah kuis dikumpulkan, di layar ponsel",
          caption: "Nilai & rekap jawaban",
          note: "Begitu dikumpulkan, siswa langsung melihat nilai beserta jawaban yang benar",
          kind: "mobile",
        },
        {
          src: "/image/fitur/kuis-ai/mobile-peringkat.webp",
          alt: "Papan peringkat kuis siswa dengan podium tiga besar, di layar ponsel",
          caption: "Papan peringkat",
          note: "Peringkat antar siswa membuat kuis terasa seperti kompetisi yang sehat",
          kind: "mobile",
        },
      ],
      faq: [
        {
          q: "Dari mana AI mengambil bahan soalnya?",
          a: "Dari materi yang guru berikan sendiri — unggahan berkas, jurnal guru di sistem, atau tulisan langsung. AI menyusun soal berdasarkan rujukan itu, bukan mengarang di luar materi.",
        },
        {
          q: "Bagaimana kalau ada soal yang kurang tepat?",
          a: "Setiap soal hasil generate dapat disunting satu per satu, atau dibangkitkan ulang, sebelum disimpan ke bank soal.",
        },
        {
          q: "Bagaimana siswa mengerjakan kuisnya?",
          a: "Kuis yang sudah dijadwalkan dikerjakan siswa melalui halaman peserta, dan hasilnya masuk ke rekap kuis untuk guru.",
        },
      ],
      metaTitle: "Kuis AI — Generate Soal Otomatis untuk Sekolah",
      metaDescription:
        "Buat soal kuis dan flashcard otomatis dengan AI dari materi guru — sunting, simpan ke bank soal, jadwalkan, dan rekap hasilnya dalam satu alur di CARDS.",
      keywords: [
        "generate soal dengan ai",
        "aplikasi kuis sekolah",
        "bank soal digital",
        "ai untuk guru",
        "membuat soal otomatis",
      ],
    },
  },
];
